import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JsonResponse } from '../../model/jsonResponse.model';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';
import { properties } from '../../../environments/properties';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  isSidebarPinned = false;
  isSidebarToggeled = false;
  private isLoggedIn: boolean = false;

  private readonly storeLogoSubject = new BehaviorSubject<string | null>(
    this.returnStoreLogo(),
  );
  readonly storeLogo$ = this.storeLogoSubject.asObservable();

  constructor(
    private readonly router: Router,
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
  ) {}

  toggleSidebar() {
    this.isSidebarToggeled = !this.isSidebarToggeled;
  }

  toggleSidebarPin() {
    this.isSidebarPinned = !this.isSidebarPinned;
  }

  getSidebarStat() {
    return {
      isSidebarPinned: this.isSidebarPinned,
      isSidebarToggeled: this.isSidebarToggeled,
    };
  }

  login(datos: JsonResponse) {
    this.isLoggedIn = true;
    sessionStorage.setItem('token', datos.data.token);
    sessionStorage.setItem('company', datos.data.usuario.adm_company);
    sessionStorage.setItem('menus', JSON.stringify(datos.data.menus));
    sessionStorage.setItem('user-name', datos.data.usuario.adm_name);
    sessionStorage.setItem('adm_state_id', datos.data.usuario.adm_state_id);

    const storeId = datos.data.usuario?.adm_store_id;
    if (storeId != null) {
      sessionStorage.setItem('store-id', String(storeId));
      const logoUrl =
        datos.data.storeLogoUrl ||
        datos.data.usuario?.storeLogoUrl ||
        datos.data.usuario?.sto_logo ||
        null;
      this.updateStoreLogo(logoUrl);
    } else {
      sessionStorage.removeItem('store-id');
      this.updateStoreLogo(null);
    }
  }

  logout() {
    this.isLoggedIn = false;
  }

  returnEmpresa(): number {
    return Number(sessionStorage.getItem('company'));
  }

  returnMenu() {
    return sessionStorage.getItem('menus');
  }

  returnUser(): string {
    return String(sessionStorage.getItem('user-name'));
  }

  returnCompany(): string {
    return String(sessionStorage.getItem('company'));
  }

  returnToken(): string {
    return String(sessionStorage.getItem('token'));
  }

  returnStateId(): string | null {
    return sessionStorage.getItem('adm_state_id');
  }

  returnStoreId(): string | null {
    return sessionStorage.getItem('store-id');
  }

  returnStoreLogo(): string | null {
    return sessionStorage.getItem('store-logo-url');
  }

  returnStoreLogoTimestamp(): number | null {
    const ts = sessionStorage.getItem('store-logo-timestamp');
    return ts ? Number(ts) : null;
  }

  updateStoreLogo(url: string | null): void {
    if (url) {
      sessionStorage.setItem('store-logo-url', url);
      sessionStorage.setItem('store-logo-timestamp', String(Date.now()));
    } else {
      sessionStorage.removeItem('store-logo-url');
      sessionStorage.removeItem('store-logo-timestamp');
    }
    this.storeLogoSubject.next(url);
  }

  fetchMyLogo(apiService: any, callback?: (url: string | null) => void): void {
    if (!this.returnStoreId()) {
      this.updateStoreLogo(null);
      callback?.(null);
      return;
    }
    apiService.consumirWSGet(properties.serviceGetMyLogo, null, (res: any) => {
      let url: string | null = null;
      if (res && res.estado === properties.codeOK && res.data) {
        if (typeof res.data === 'string') {
          url = res.data;
        } else if (res.data.storeLogoUrl) {
          url = res.data.storeLogoUrl;
        } else if (res.data.url) {
          url = res.data.url;
        }
      }
      this.updateStoreLogo(url);
      callback?.(url);
    });
  }

  isAuthenticated(): boolean {
    if (!this.isLoggedIn && sessionStorage.getItem('token')) {
      this.isLoggedIn = true;
    }
    return this.isLoggedIn;
  }

  eliminarSesion() {
    this.updateStoreLogo(null);
    sessionStorage.clear();
    this.logout();
    this.router.navigate(['/login']);
  }

  /**
   *
   * @param titulo Titulo del mensaje
   * @param mensaje Mensaje detalle del toast
   * @param severity Color del toast 1: success, 2: info, 3: warning, 4: error
   */
  mostrarToast(titulo: string, mensaje: string, severity?: number) {
    let severityTxt: string;
    let keyCounter: number = Number(sessionStorage.getItem('counterToast')) + 1;
    sessionStorage.setItem('counterToast', String(keyCounter));

    switch (severity) {
      case 1:
        severityTxt = 'success';
        break;
      case 2:
        severityTxt = 'info';
        break;
      case 3:
        severityTxt = 'warn';
        break;
      case 4:
        severityTxt = 'error';
        break;
      default:
        severityTxt = 'success';
        break;
    }
    this.clear();
    this.messageService.add({
      severity: severityTxt,
      summary: titulo,
      detail: mensaje,
    });
  }
  mostrarConfirmacion(mensaje: string, icon?: number) {
    let icono: string;
    switch (icon) {
      case 1: {
        icono = 'fa-solid fa-check-circle';
        break;
      }
      case 2: {
        icono = 'fa-solid fa-exclamation-triangle';
        break;
      }
      case 3: {
        icono = 'fa-solid fa-info-circle';
        break;
      }
      case 4: {
        icono = 'fa-solid fa-exclamation-circle';
        break;
      }
      case 5: {
        icono = 'fa-solid fa-minus-circle';
        break;
      }
      case 6: {
        icono = 'fa-solid fa-plus-circle';
        break;
      }
      case 7: {
        icono = 'fa-solid fa-question-circle';
        break;
      }
      case 8: {
        icono = 'fa-solid fa-times-circle';
        break;
      }
      case 9: {
        icono = 'fa-solid fa-user-circle';
        break;
      }
    }
    return new Promise((accept) => {
      this.confirmationService.confirm({
        header: 'Confirmar',
        message: mensaje,
        icon: icono,
        accept: () => {
          accept(accept);
        },
      });
    });
  }

  clear() {
    this.messageService.clear();
  }
}
